import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Building2, ReceiptText, Award, Globe2, UtensilsCrossed, ShieldCheck,
  BadgeCheck, Store, Factory, Handshake, ArrowRight, Check, Info, ArrowUpRight, Sparkles,
} from "lucide-react";

const CATEGORIES = [
  {
    num: "01",
    icon: Building2,
    title: "BUSINESS REGISTRATION",
    tagline: "START YOUR BUSINESS THE RIGHT WAY",
    desc: "Applicable business structures and registrations for a strong legal foundation.",
    accent: "#FF8800",
    detail:
      "Choosing the right structure shapes taxation, liability and growth. LaunchLab can help entrepreneurs with applicable business structures and registrations such as:",
    bullets: [
      "Private Limited Company",
      "LLP (Limited Liability Partnership)",
      "Partnership Firm",
      "Proprietorship",
      "Section 8 Company",
      "Other applicable business registrations",
    ],
    flow: ["IDEA", "BUSINESS STRUCTURE", "REGISTRATION", "BUSINESS"],
  },
  {
    num: "02",
    icon: ReceiptText,
    title: "GST & TAX REGISTRATIONS",
    tagline: "BUILD A TAX-READY BUSINESS",
    desc: "GST, PAN, TAN and tax-related documentation support.",
    accent: "#00B0FF",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/a822f7256d053ef7e6b591c79120c3149df4e864c75b91e891baa9692802263c.jpeg",
    detail:
      "A tax-ready business earns trust from customers, platforms and partners. LaunchLab's support may include:",
    bullets: [
      "GST Registration",
      "GST-related registration assistance",
      "PAN",
      "TAN",
      "Tax-related documentation support",
      "Applicable tax registrations",
    ],
  },
  {
    num: "03",
    icon: Award,
    title: "MSME / UDYAM",
    tagline: "RECOGNIZE YOUR BUSINESS",
    desc: "Udyam / MSME registration for eligible businesses.",
    accent: "#00E676",
    detail:
      "For eligible businesses, MSME (Udyam) registration can unlock access to applicable government schemes, priority-sector benefits and business opportunities. LaunchLab helps eligible businesses understand and apply for:",
    bullets: [
      "Udyam / MSME Registration",
      "Understanding applicable government schemes",
      "MSME-related documentation support",
    ],
  },
  {
    num: "04",
    icon: Globe2,
    title: "IMPORT & EXPORT",
    tagline: "TAKE YOUR BUSINESS GLOBAL",
    desc: "Registrations and documentation for international trade.",
    accent: "#FF8800",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/eff4ac89afc241d1893a4b0f6128ddd0b1234adaeaab7ddea27f4a9593a5e714.jpeg",
    detail:
      "For businesses involved in international trade, LaunchLab can assist with applicable registrations and documentation such as:",
    bullets: [
      "Import Export Code (IEC)",
      "Export-related registrations",
      "Trade documentation",
      "Applicable product / export certifications",
      "International business documentation",
    ],
  },
  {
    num: "05",
    icon: UtensilsCrossed,
    title: "FOOD BUSINESS",
    tagline: "BUILD A COMPLIANT FOOD BUSINESS",
    desc: "FSSAI registration, licenses and food-related compliance support.",
    accent: "#00B0FF",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/fdc6f01a4cdb161a39c0fd2b8729572d724a04b65595dc15438f358838f2edd8.jpeg",
    detail:
      "For eligible food businesses, LaunchLab's assistance may include:",
    bullets: [
      "FSSAI Registration",
      "FSSAI License",
      "Food-related documentation",
      "Applicable food certifications",
      "Packaging and labeling compliance support",
    ],
    note: "Requirements vary according to the nature, scale and location of the food business.",
  },
  {
    num: "06",
    icon: ShieldCheck,
    title: "INTELLECTUAL PROPERTY",
    tagline: "PROTECT WHAT YOU CREATE",
    desc: "Trademark, copyright, patent, design and brand protection coordination.",
    accent: "#00E676",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/83e073f61d9f5d153475cf0bd8d3824c4d8899a8118eca65ed0137096167c0f6.jpeg",
    headline: "YOUR IDEA. YOUR BRAND. YOUR INTELLECTUAL PROPERTY.",
    detail:
      "What you create deserves protection. LaunchLab can help coordinate applications and professional support for:",
    bullets: [
      "Trademark",
      "Copyright",
      "Patent",
      "Design Registration",
      "Brand protection",
    ],
  },
  {
    num: "07",
    icon: BadgeCheck,
    title: "DIGITAL & BUSINESS CERTIFICATIONS",
    tagline: "BUILD TRUST THROUGH CERTIFICATION",
    desc: "Identify and coordinate applicable ISO, quality and industry certifications.",
    accent: "#FF8800",
    detail:
      "Depending on the business, LaunchLab can help identify and coordinate applicable certifications such as ISO, quality management, information security, environmental and industry-specific certifications.",
    bullets: [
      "ISO certifications",
      "Quality management certifications",
      "Information security certifications",
      "Environmental certifications",
      "Industry-specific certifications",
    ],
    flow: ["LAUNCHLAB", "DOCUMENTATION & COORDINATION", "ACCREDITED CERTIFICATION BODY", "CERTIFICATION"],
    note: "LaunchLab does not issue ISO or other third-party certifications — these are granted only by accredited certification bodies.",
  },
  {
    num: "08",
    icon: Store,
    title: "SHOP & ESTABLISHMENT / LOCAL",
    tagline: "GET READY TO OPERATE",
    desc: "Local registrations and permissions based on state, city and activity.",
    accent: "#00B0FF",
    detail:
      "Depending on the state, city and business activity, LaunchLab can help identify applicable local registrations and permissions such as:",
    bullets: [
      "Shops & Establishments registration",
      "Trade licenses",
      "Local municipal permissions",
      "Professional Tax, where applicable",
      "Other applicable local registrations",
    ],
    note: "Requirements vary by state, municipality, business type and location.",
  },
  {
    num: "09",
    icon: Factory,
    title: "INDUSTRY-SPECIFIC LICENSES",
    tagline: "SPECIALIZED BUSINESSES NEED SPECIALIZED APPROVALS",
    desc: "Approvals that depend on your exact industry — explore the selector below.",
    accent: "#00E676",
    detail:
      "Every regulated industry carries its own approval landscape. LaunchLab helps you understand which specialized licenses and approvals may apply to your exact activity.",
    bullets: ["Use the interactive industry selector below this grid to explore."],
    note: "Applicable registrations and approvals depend on the exact business activity, location, products/services and regulatory requirements.",
  },
  {
    num: "10",
    icon: Handshake,
    title: "STARTUP & GOVERNMENT PROGRAMS",
    tagline: "CONNECT WITH THE RIGHT ECOSYSTEM",
    desc: "Understand relevant programs, registrations and recognition.",
    accent: "#FF8800",
    detail:
      "Where applicable, LaunchLab can assist businesses in understanding and applying for relevant programs, registrations or recognition such as:",
    bullets: [
      "Startup India-related registration / support",
      "MSME / Udyam",
      "Government entrepreneurship programs",
      "Applicable subsidies / schemes",
      "Industry development programs",
    ],
    note: "LaunchLab does not promise approval, subsidy eligibility or government benefits — all decisions rest with the respective authorities.",
  },
];

const INDUSTRIES = [
  "Food & Beverages", "Manufacturing", "Healthcare", "Education", "Logistics",
  "E-commerce", "Retail", "Technology", "Finance", "Import & Export",
  "Construction", "Professional Services", "Other Industries",
];

function RegDetailModal({ item, onClose }) {
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#0D111D] border-white/10 text-slate-100 max-w-2xl max-h-[88vh] overflow-y-auto p-0"
        data-testid="reg-detail-modal"
      >
        {item && (
          <div>
            {item.image && (
              <div className="relative h-52 sm:h-64">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D111D] via-[#0D111D]/30 to-transparent" />
              </div>
            )}
            <div className="p-6 sm:p-8">
              <DialogHeader>
                <DialogDescription className="sr-only">
                  {`${item.title} — ${item.tagline}. Details of LaunchLab's facilitation support.`}
                </DialogDescription>
                <div className="flex items-center gap-3.5">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${item.accent}14`, border: `1px solid ${item.accent}33` }}
                  >
                    <item.icon className="w-5 h-5" style={{ color: item.accent }} strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase" style={{ color: item.accent }}>
                      {item.num} — {item.tagline}
                    </p>
                    <DialogTitle className="font-display text-xl sm:text-2xl font-bold text-white text-left" data-testid="reg-detail-title">
                      {item.title}
                    </DialogTitle>
                  </div>
                </div>
              </DialogHeader>
              {item.headline && (
                <p className="mt-4 font-display font-bold text-base sm:text-lg text-gradient-saffron">{item.headline}</p>
              )}
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mt-4" data-testid="reg-detail-text">
                {item.detail}
              </p>
              {item.bullets?.length > 0 && (
                <ul className="mt-5 grid sm:grid-cols-2 gap-2.5" data-testid="reg-detail-bullets">
                  {item.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-slate-200 glass rounded-lg px-3.5 py-2.5">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: item.accent }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {item.flow && (
                <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="reg-detail-flow">
                  {item.flow.map((f, i) => (
                    <div key={f} className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1.5 rounded-lg font-mono-tech text-[10px] tracking-[0.14em] ${
                          i === item.flow.length - 1
                            ? "bg-[#00E676]/10 border border-[#00E676]/40 text-[#00E676]"
                            : "glass text-slate-200"
                        }`}
                      >
                        {f}
                      </span>
                      {i < item.flow.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-[#FF8800]" />}
                    </div>
                  ))}
                </div>
              )}
              {item.note && (
                <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-[#FF8800]/25 bg-[#FF8800]/[0.05] px-4 py-3" data-testid="reg-detail-note">
                  <Info className="w-4 h-4 text-[#FF8800] shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">{item.note}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function Categories() {
  const [selected, setSelected] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [dynamic, setDynamic] = useState([]);

  useEffect(() => {
    api
      .get("/regcategories")
      .then((r) =>
        setDynamic(
          r.data.map((d) => ({
            ...d,
            icon: Sparkles,
            bullets: (d.bullets || "").split("\n").filter(Boolean),
          }))
        )
      )
      .catch(() => {});
  }, []);

  return (
    <section id="reg-categories" data-testid="reg-categories-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,136,0,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-14"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">What LaunchLab Helps With</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="reg-categories-headline">
            ONE BUSINESS. MANY REQUIREMENTS. <span className="text-gradient-saffron">ONE SUPPORT ECOSYSTEM.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Different businesses have different legal, regulatory and certification
            requirements. LaunchLab helps entrepreneurs understand what may be applicable and
            assists with documentation and application coordination. Select any category to explore.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reg-categories-grid">
          {CATEGORIES.concat(dynamic).map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
            >
              <button
                onClick={() => setSelected(c)}
                data-testid={`reg-category-${c.num}`}
                className="card-beam group relative w-full h-full text-left rounded-2xl border border-slate-800 bg-[#0D111D]/90 p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8800]"
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
                  style={{ boxShadow: `inset 0 0 0 1px ${c.accent}55, 0 0 35px ${c.accent}18` }}
                />
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{ background: `${c.accent}14`, border: `1px solid ${c.accent}33` }}
                  >
                    <c.icon className="w-5 h-5" style={{ color: c.accent }} strokeWidth={1.8} />
                  </div>
                  <span className="font-display font-extrabold text-2xl" style={{ color: `${c.accent}55` }}>
                    {c.num}
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold tracking-wide text-white">{c.title}</h3>
                <p className="mt-1 font-mono-tech text-[10px] tracking-[0.18em] uppercase" style={{ color: c.accent }}>
                  {c.tagline}
                </p>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                <p className="mt-3 flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-slate-500 group-hover:text-white transition-colors">
                  Explore <ArrowUpRight className="w-3 h-3" />
                </p>
              </button>
            </motion.div>
          ))}
        </div>

        {/* Interactive industry selector */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="mt-16 glass rounded-2xl p-6 sm:p-10"
          data-testid="industry-selector"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-3">Industry Selector</p>
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
            WHICH INDUSTRY ARE YOU IN?
          </h3>
          <p className="text-sm text-slate-400 mb-7">Select your industry to understand how requirements apply.</p>
          <div className="flex flex-wrap gap-2.5">
            {INDUSTRIES.map((ind) => (
              <button
                key={ind}
                data-testid={`industry-option-${ind.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setIndustry(ind)}
                aria-pressed={industry === ind}
                className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                  industry === ind
                    ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]"
                    : "glass text-slate-300 hover:text-white hover:border-[#00B0FF]/40"
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
          {industry && (
            <motion.div
              key={industry}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-7 rounded-xl border border-[#00B0FF]/30 bg-[#00B0FF]/[0.05] px-5 py-4 flex items-start gap-3"
              data-testid="industry-selector-result"
            >
              <Info className="w-4 h-4 text-[#00B0FF] shrink-0 mt-0.5" />
              <p className="text-sm text-slate-200 leading-relaxed">
                <span className="font-semibold text-white">{industry}:</span> Applicable
                registrations and approvals depend on the exact business activity, location,
                products/services and regulatory requirements. LaunchLab can help you identify
                what applies to your specific case.
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>

      <RegDetailModal item={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
